import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@repo/ui/src/components/ui/card';
import { Button } from '@repo/ui/src/components/ui/button';
import { Badge } from '@repo/ui/src/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { CreditCard, Link2Off } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fetchApi } from '../../../lib/api';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { useReconcileStatus } from '../../../hooks/useReconcileStatus';
import { useBankAccounts, type BankAccount } from '../../../hooks/useBankAccounts';

export function BankTab() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { data: accounts = [], isLoading } = useBankAccounts();
  const { data: reconcileStatus } = useReconcileStatus();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);
  const [linking, setLinking] = useState(false);

  const linkMutation = useMutation({
    mutationFn: () =>
      fetchApi<{ hostedLinkUrl: string }>('/bank-accounts/link', { method: 'POST' }),
    onSuccess: (data) => {
      setLinking(true);
      const popup = window.open(data.hostedLinkUrl, 'sepay-bankhub', 'width=500,height=700');
      const interval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(interval);
          setLinking(false);
          queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
        }
      }, 1000);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('bank.linkError'));
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (bankAccountXid: string) =>
      fetchApi<{ hostedLinkUrl: string }>('/bank-accounts/unlink', {
        method: 'POST',
        body: JSON.stringify({ bankAccountXid }),
      }),
    onSuccess: (data) => {
      const popup = window.open(data.hostedLinkUrl, 'sepay-bankhub', 'width=500,height=700');
      const interval = setInterval(() => {
        if (popup?.closed) {
          clearInterval(interval);
          queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
        }
      }, 1000);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('bank.unlinkError'));
    },
  });

  // THI-347: the tài khoản nhận học phí. The API validates the xid against
  // SePay's live list; on success the list (with its `isPrimary` flags) and
  // the org settings (which carry the printed number) are refetched.
  const [primaryCandidate, setPrimaryCandidate] = useState<BankAccount | null>(null);
  const primaryMutation = useMutation({
    mutationFn: (bankAccountXid: string) =>
      fetchApi<BankAccount[]>('/bank-accounts/primary', { method: 'PUT', body: JSON.stringify({ bankAccountXid }) }),
    onSuccess: () => {
      setPrimaryCandidate(null);
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
      // Cached invoice QRs (`['invoices', id, 'qr']`, 60s stale) print the old number.
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'invoices' && q.queryKey[2] === 'qr' });
      toast.success(t('bank.primarySaved'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('bank.primaryError'));
    },
  });
  const severalActive = accounts.filter((a) => a.isActive).length > 1;

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('bank.title')}</CardTitle>
        <CardDescription>
          {t('bank.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-surface-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : accounts.length > 0 ? (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div
                key={account.xid}
                // Column on a phone: with a second account the row carries a badge,
                // "Dùng tài khoản này" and "Huỷ", which no longer fit beside the holder name.
                className="flex flex-col gap-3 p-4 border border-surface-200 rounded-lg sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-brand-600" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-surface-900">{account.bankName}</p>
                    <p className="text-xs text-surface-500">
                      {account.accountNumber} — {account.accountHolderName}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {account.isPrimary ? (
                    <Badge variant="outline" className="border-brand-200 bg-brand-50 text-brand-700">
                      {t('bank.primary')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-green-600 border-green-200 bg-green-50"
                    >
                      {t('bank.connected')}
                    </Badge>
                  )}
                  {!account.isPrimary && account.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPrimaryCandidate(account)}
                      disabled={primaryMutation.isPending}
                      className="text-brand-700"
                    >
                      {t('bank.usePrimary')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => unlinkMutation.mutate(account.xid)}
                    disabled={unlinkMutation.isPending}
                    className="text-surface-500 hover:text-red-600"
                  >
                    <Link2Off className="w-4 h-4 mr-1" strokeWidth={1.8} />
                    {t('bank.unlink')}
                  </Button>
                </div>
              </div>
            ))}
            {severalActive && <p className="text-xs text-surface-500">{t('bank.primaryHint')}</p>}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-14 h-14 bg-surface-100 flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-6 h-6 text-surface-400" strokeWidth={1.8} />
            </div>
            <p className="text-sm font-medium text-surface-700 mb-1">
              {t('bank.emptyTitle')}
            </p>
            <p className="text-xs text-surface-400 max-w-xs mx-auto">
              {t('bank.emptyDescription')}
            </p>
          </div>
        )}

        {reconcileStatus?.linked && (
          <div className="mt-4 p-4 border border-surface-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  reconcileStatus.status === 'active'
                    ? 'bg-green-500'
                    : reconcileStatus.status === 'paused'
                      ? 'bg-amber-500'
                      : 'bg-surface-400'
                }`}
              />
              <p className="text-sm font-medium text-surface-900">
                {t(`bank.reconcile.${reconcileStatus.status}Label`)}
              </p>
            </div>

            {reconcileStatus.status === 'active' &&
              (reconcileStatus.unlimited ? (
                <p className="mt-2 text-xs text-surface-500">{t('bank.reconcile.unlimited')}</p>
              ) : (
                <div className="mt-2">
                  <p className="text-xs text-surface-500 mb-1">
                    {t('bank.reconcile.usage', {
                      used: reconcileStatus.used,
                      quota: reconcileStatus.quota,
                    })}
                  </p>
                  <div className="w-full h-1.5 bg-surface-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-600 rounded-full"
                      style={{
                        width: `${
                          reconcileStatus.quota > 0
                            ? Math.min(100, (reconcileStatus.used / reconcileStatus.quota) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ))}

            {reconcileStatus.status === 'off' && (
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-xs text-surface-500">{t('bank.reconcile.offSubtext')}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs shrink-0"
                  onClick={() => openUpgradeDialog()}
                >
                  {t('bank.reconcile.offCta')}
                </Button>
              </div>
            )}

            {reconcileStatus.status === 'paused' && (
              <p className="mt-2 text-xs text-surface-500">{t('bank.reconcile.pausedSubtext')}</p>
            )}
          </div>
        )}

        <div className="mt-4">
          <Button
            onClick={() => linkMutation.mutate()}
            disabled={linkMutation.isPending || linking}
            className="w-full h-10 text-sm font-semibold"
          >
            {linking ? t('bank.linking') : t('bank.linkButton')}
          </Button>
        </div>
      </CardContent>
    </Card>

      <AlertDialog open={primaryCandidate !== null} onOpenChange={(o) => { if (!o) setPrimaryCandidate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bank.primaryConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bank.primaryConfirmBody', { bank: primaryCandidate?.bankName ?? '', account: primaryCandidate?.accountNumber ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={primaryMutation.isPending}>{t('bank.primaryCancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={primaryMutation.isPending}
              onClick={(e) => { e.preventDefault(); if (primaryCandidate) primaryMutation.mutate(primaryCandidate.xid); }}
            >
              {t('bank.primaryConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
